import React, { useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
  Button,
  CircularProgress,
  Tooltip
} from '@mui/material';
import MessageFormatter from '../MessageFormatter';
import logo from '../../assets/images/Supervoima-Logo-White.svg';
import {
  TrendingUp as HighVarianceIcon
} from '@mui/icons-material';

// Distribution cloud - renders as semi-transparent rounded area
function DistributionCloud({ distribution }) {
  if (!distribution) return null;

  const values = [1, 2, 3, 4, 5].map(s => distribution[s.toString()] || 0);
  const maxVal = Math.max(...values, 1);
  const normalized = values.map(v => (v / maxVal) * 80);
  const points = normalized.map((h, i) => ({ x: 10 + i * 20, y: 100 - h }));

  let pathD = `M 0,100`;
  pathD += ` C 0,100 ${points[0].x - 5},${points[0].y} ${points[0].x},${points[0].y}`;
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i];
    const p1 = points[i + 1];
    pathD += ` C ${p0.x + 5},${p0.y} ${p1.x - 5},${p1.y} ${p1.x},${p1.y}`;
  }
  const lastP = points[points.length - 1];
  pathD += ` C ${lastP.x + 5},${lastP.y} 100,100 100,100 Z`;

  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>
      <path d={pathD} fill="rgb(1, 28, 45)" opacity="0.2" />
    </svg>
  );
}

export default function QuestionPrioritySection({
  questionPriorities = [],
  priorityInsights = {},
  onGenerateAnalysis: onGeneratePriorityInsights, // Renaming prop for clarity
  generating = false,
  analysisTimestamp = null,
  scope = 'organization',
  companyName = '',
  userName = ''
}) {
  const getHeaderText = () => {
    const name = companyName || 'Organization';
    return `AI Priorities - ${name}`;
  };
  // Format timestamp to DD.MM.YYYY HH:MM format (Finnish standard with time)
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}.${month}.${year} ${hours}:${minutes}`;
  };

  // Check if we have any insights to determine button text
  const hasAnyInsights = Object.keys(priorityInsights).length > 0;
  const [orderBy, setOrderBy] = useState('priority_score');
  const [order, setOrder] = useState('desc');

  if (!questionPriorities?.length) {
    return null;
  }

  // Calculate max priority score for percentage calculation
  const maxPriorityScore = Math.max(...questionPriorities.map(q => q.priority_score));

  const getVarianceIcon = (variationLevel) => {
    switch (variationLevel) {
      case 'high':
        return <HighVarianceIcon sx={{ fontSize: 16, color: 'warning.main' }} />;
      default:
        return null;
    }
  };

  const getVarianceText = (question) => {
    // Only show consensus for questions with 2+ responses
    if (question.response_count < 2) {
      return '';
    }

    if (question.variation_level === 'high') {
      return 'Mixed perspectives';
    } else if (question.variation_level === 'low') {
      return 'Strong consensus';
    }
    return '';
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedQuestions = [...questionPriorities].sort((a, b) => {
    if (orderBy === 'priority_score') {
      return order === 'desc' ? b.priority_score - a.priority_score : a.priority_score - b.priority_score;
    }
    if (orderBy === 'avg_score') {
      return order === 'desc' ? b.avg_score - a.avg_score : a.avg_score - b.avg_score;
    }
    if (orderBy === 'avg_importance') {
      return order === 'desc' ? b.avg_importance - a.avg_importance : a.avg_importance - b.avg_importance;
    }
    return 0;
  });

  return (
    <Paper elevation={4} sx={{ p: 0, mb: 6, borderRadius: 3, overflow: 'hidden' }}>
      {/* Blue Header Bar with Logo, Title, and Generate Button */}
      <Box sx={{
        backgroundColor: 'rgb(1, 28, 45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 3,
        py: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <img
            src={logo}
            alt="Supervoima Logo"
            style={{ height: '36px', width: 'auto' }}
          />
          <Typography variant="h5" sx={{
            color: 'white',
            fontFamily: 'Bebas Neue',
            fontWeight: 'normal',
            letterSpacing: 1
          }}>
            {getHeaderText()}
          </Typography>
        </Box>
        {/* Generate/Regenerate button on blue bar */}
        {onGeneratePriorityInsights && (
          <Button
            onClick={onGeneratePriorityInsights}
            disabled={generating}
            variant="outlined"
            size="small"
            startIcon={generating ? <CircularProgress size={16} sx={{ color: 'white' }} /> : null}
            sx={{
              color: 'white',
              borderColor: 'rgba(255,255,255,0.6)',
              '&:hover': {
                borderColor: 'white',
                backgroundColor: 'rgba(255,255,255,0.1)'
              },
              '&:disabled': {
                borderColor: 'rgba(255,255,255,0.3)',
                color: 'rgba(255,255,255,0.5)'
              }
            }}
          >
            {generating ? 'Generating...' : (hasAnyInsights ? 'Regenerate Insights' : 'Generate Insights')}
          </Button>
        )}
      </Box>

      {/* Content Area */}
      <Box sx={{ p: 5 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" sx={{ color: 'rgb(1, 28, 45)', fontFamily: 'Bebas Neue', fontWeight: 'normal', mb: 2 }}>
            PRIORITY ANALYSIS
          </Typography>
          {/* Timestamp chip - shown when insights exist */}
          {analysisTimestamp && hasAnyInsights && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              <Box component="span" sx={{
                fontSize: '0.875rem',
                color: 'text.secondary',
                px: 1.5,
                py: 0.5,
                border: '1px solid rgba(0,0,0,0.23)',
                borderRadius: '16px'
              }}>
                Generated: {formatTimestamp(analysisTimestamp)}
              </Box>
            </Box>
          )}
        </Box>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Each question answered sorted by highest priority. Click on Generate AI Insights to get key facts and recommended actions for the most important topics based on all consolidated answers.
      </Typography>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow sx={{
              '& th:first-of-type': {
                borderLeft: '2px solid rgb(1, 28, 45)'
              },
              '& th:last-of-type': {
                borderRight: '2px solid rgb(1, 28, 45)'
              }
            }}>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'rgb(1, 28, 45)', color: 'white', textAlign: 'center', width: '100px' }}>
                <TableSortLabel
                  active={orderBy === 'priority_score'}
                  direction={orderBy === 'priority_score' ? order : 'asc'}
                  onClick={() => handleRequestSort('priority_score')}
                  sx={{
                    '& .MuiTableSortLabel-icon': { color: 'white !important' },
                    color: 'white',
                    '&.Mui-active': { color: 'white' },
                    '&:hover': { color: 'rgba(255, 255, 255, 0.7)' }
                  }}
                >
                  Priority
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'rgb(1, 28, 45)', color: 'white' }}>Question</TableCell>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'rgb(1, 28, 45)', color: 'white', textAlign: 'center', width: '100px' }}>
                <TableSortLabel
                  active={orderBy === 'avg_score'}
                  direction={orderBy === 'avg_score' ? order : 'asc'}
                  onClick={() => handleRequestSort('avg_score')}
                  sx={{
                    '& .MuiTableSortLabel-icon': { color: 'white !important' },
                    color: 'white',
                    '&.Mui-active': { color: 'white' },
                    '&:hover': { color: 'rgba(255, 255, 255, 0.7)' }
                  }}
                >
                  Score
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'rgb(1, 28, 45)', color: 'white', textAlign: 'center', width: '120px' }}>
                <TableSortLabel
                  active={orderBy === 'avg_importance'}
                  direction={orderBy === 'avg_importance' ? order : 'asc'}
                  onClick={() => handleRequestSort('avg_importance')}
                  sx={{
                    '& .MuiTableSortLabel-icon': { color: 'white !important' },
                    color: 'white',
                    '&.Mui-active': { color: 'white' },
                    '&:hover': { color: 'rgba(255, 255, 255, 0.7)' }
                  }}
                >
                  Importance
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedQuestions.map((question, index) => {
              const insights = priorityInsights[question.question_id];
              const hasInsights = insights && insights.markdown_content;

              // Calculate priority percentage
              const priorityPercentage = maxPriorityScore > 0 ? Math.round((question.priority_score / maxPriorityScore) * 100) : 0;

              const rows = [];

              // Main question row
              rows.push(
                <TableRow
                  key={`${question.question_id}-main`}
                  sx={{
                    '& td': {
                      borderTop: '2px solid rgb(1, 28, 45)',
                      borderBottom: hasInsights ? 'none' : '2px solid rgb(1, 28, 45)'
                    },
                    '& td:first-of-type': {
                      borderLeft: '2px solid rgb(1, 28, 45)'
                    },
                    '& td:last-of-type': {
                      borderRight: '2px solid rgb(1, 28, 45)'
                    }
                  }}
                >
                  <TableCell align="center">
                    <Box sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}>
                      <Box sx={{
                        width: '70px',
                        height: '70px',
                        borderRadius: '50%',
                        backgroundColor: question.priority_score >= 12.0 ? 'error.main' : question.priority_score >= 6.0 ? 'warning.main' : 'success.main',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: 0.5
                      }}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: '0.7rem',
                            fontWeight: 'bold',
                            color: 'white',
                            lineHeight: 1,
                            textAlign: 'center'
                          }}
                        >
                          {question.priority_score >= 12.0 ? 'High' : question.priority_score >= 6.0 ? 'Medium' : 'Low'}
                        </Typography>
                        <Tooltip title="Topic priority in relation to top priority identified in analysis" arrow>
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: '1.1rem',
                              fontWeight: 'bold',
                              color: 'white',
                              lineHeight: 1,
                              textAlign: 'center',
                              cursor: 'help'
                            }}
                          >
                            {priorityPercentage}%
                          </Typography>
                        </Tooltip>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary" sx={{
                      fontSize: '0.7rem',
                      fontWeight: 'medium',
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      display: 'block',
                      mb: 0.5
                    }}>
                      {question.dimension ? `${question.dimension.toUpperCase()} - ${question.axis_name}` : question.axis_name}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.85rem', mb: 1 }}>
                      {question.question_text}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                      {getVarianceText(question) && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {getVarianceIcon(question.variation_level)}
                          <Typography variant="caption" sx={{
                            fontStyle: 'italic',
                            color: question.variation_level === 'high' ? 'warning.main' : 'success.main',
                            fontSize: '0.7rem'
                          }}>
                            {getVarianceText(question)}
                          </Typography>
                        </Box>
                      )}
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                        ({question.response_count} responses)
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 'medium' }}>
                        {question.avg_score.toFixed(1)} / 5.0
                      </Typography>
                      {/* Average score bar */}
                      <Box sx={{ width: '95px', height: '10px', backgroundColor: 'grey.300', borderRadius: '5px', overflow: 'hidden' }}>
                        <Box sx={{
                          width: `${(question.avg_score / 5.0) * 100}%`,
                          height: '100%',
                          backgroundColor: question.avg_score >= 4 ? 'success.main' : question.avg_score >= 2.5 ? 'warning.main' : 'error.main',
                          borderRadius: '5px',
                          transition: 'width 0.3s ease'
                        }} />
                      </Box>
                      {/* Distribution cloud below the bar */}
                      {question.score_distribution && (
                        <Box sx={{ width: '95px', height: '22px', mt: 0.5, position: 'relative', borderRadius: '3px', overflow: 'hidden', backgroundColor: 'grey.100' }}>
                          <DistributionCloud distribution={question.score_distribution} />
                        </Box>
                      )}
                      {question.score_distribution && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                          Distribution
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 'medium' }}>
                        {question.avg_importance.toFixed(1)} / 5.0
                      </Typography>
                      {/* Average importance bar */}
                      <Box sx={{ width: '95px', height: '10px', backgroundColor: 'grey.300', borderRadius: '5px', overflow: 'hidden' }}>
                        <Box sx={{
                          width: `${(question.avg_importance / 5.0) * 100}%`,
                          height: '100%',
                          backgroundColor: question.avg_importance >= 4 ? 'success.main' : question.avg_importance >= 2.5 ? 'warning.main' : 'error.main',
                          borderRadius: '5px',
                          transition: 'width 0.3s ease'
                        }} />
                      </Box>
                      {/* Distribution cloud below the bar */}
                      {question.importance_distribution && (
                        <Box sx={{ width: '95px', height: '22px', mt: 0.5, position: 'relative', borderRadius: '3px', overflow: 'hidden', backgroundColor: 'grey.100' }}>
                          <DistributionCloud distribution={question.importance_distribution} />
                        </Box>
                      )}
                      {question.importance_distribution && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                          Distribution
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );


              // Insights row (full width) - only if we have insights
              if (hasInsights) {
                rows.push(
                  <TableRow
                    key={`${question.question_id}-insights`}
                    sx={{
                      '& td': {
                        borderLeft: '2px solid rgb(1, 28, 45)',
                        borderRight: '2px solid rgb(1, 28, 45)',
                        borderBottom: '2px solid rgb(1, 28, 45)'
                      }
                    }}
                  >
                    <TableCell colSpan={4} sx={{ pt: 0, pb: 2 }}>
                      <Box sx={{
                        pl: 2,
                        pr: 2,
                        borderTop: '1px solid rgba(1, 28, 45, 0.2)',
                        pt: 2,
                        '& p': { fontSize: '0.85rem', lineHeight: 1.5, margin: '0.5rem 0' },
                        '& ul': { margin: '0.5rem 0' },
                        '& li': { fontSize: '0.85rem', lineHeight: 1.4 },
                        '& strong': { fontSize: '0.85rem', color: 'text.primary', fontWeight: 'bold' },
                        '& h3': { display: 'none' } // Hide Q# headers since question is shown above
                      }}>
                        <MessageFormatter content={insights.markdown_content} />
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              }

              return rows;
            }).flat()}
          </TableBody>
        </Table>
      </TableContainer>

        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', fontSize: '0.7rem' }}>
            Insights and recommendations generated by AI. Use results as guidance only.
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
}